import type { Scenario, ScenarioContent, ScenarioContextBlock } from "@/lib/powtoon-be/types";
import type { ScenarioAdapter, ScenarioView, ScenarioFormState } from "../types";
import { getMode } from "@/lib/scenarios/interactionModes";
import { DEFAULT_MCQ_INSTRUCTIONS } from "@/lib/scenarios/interactionModes";
import { strippedForeignFields } from "../spec";

function composeSystemPrompt(scenario: Scenario): string {
  const cb = scenario.content?.context_block;
  if (!cb) return "";
  if (cb.generated_prompt && cb.generated_prompt.trim()) return cb.generated_prompt;
  return [cb.persona, cb.knowledge, cb.instructions]
    .filter((s) => s && s.trim().length > 0)
    .join("\n\n");
}

export const liveavatarAdapter: ScenarioAdapter = {
  toView(scenario: Scenario): ScenarioView {
    const c = scenario.content ?? {};
    const cb = c.context_block;
    return {
      provider: "liveavatar",
      name: scenario.name,
      description: scenario.description,
      thumbnailUrl: scenario.thumb_url || undefined,
      systemPrompt: composeSystemPrompt(scenario),
      openingText: cb?.opening_text,
      faceId: c.avatar_id,
      voiceId: c.voice_id,
      language: c.language,
      inputs: cb?.inputs ?? [],
    };
  },
  deriveThumbnail(scenario) {
    return scenario.thumb_url || undefined;
  },
  toFormState(scenario: Scenario): Partial<ScenarioFormState> {
    const c = scenario.content ?? {};
    const cb = c.context_block;
    return {
      avatarId: c.avatar_id ?? "",
      voiceId: c.voice_id ?? "",
      language: c.language ?? "",
      persona: cb?.persona ?? "",
      knowledge: cb?.knowledge ?? "",
      instructions:
        cb?.instructions ||
        (cb?.interaction_mode === "multiple_choice"
          ? DEFAULT_MCQ_INSTRUCTIONS
          : ""),
      openingText: cb?.opening_text ?? "",
    };
  },
  collectSaveMeta(scenario) {
    const cb = scenario?.content?.context_block;
    return { initialRoleplayIds: cb?.roleplay_ids ?? [] };
  },
  toContent(state: ScenarioFormState): ScenarioContent {
    const prev = state.previous ?? {};
    const prevBlock = prev.context_block;
    const modeMeta = getMode(state.mode);
    const nextBlock: ScenarioContextBlock = {
      // Preserve backend-owned fields the editor doesn't touch.
      liveavatar_context_id: prevBlock?.liveavatar_context_id,
      questionnaire_ids: prevBlock?.questionnaire_ids,
      roleplay_ids: prevBlock?.roleplay_ids,
      roleplay_scenario_id:
        state.mode === "role_play" ? prevBlock?.roleplay_scenario_id : undefined,
      // Editor-owned fields.
      interaction_mode: state.mode,
      persona: modeMeta.showPersona ? state.persona : "",
      knowledge: state.knowledge,
      instructions: modeMeta.showInstructions ? state.instructions : "",
      opening_text: state.openingText,
      generated_prompt: state.generatedPrompt,
      // Placeholder — saveScenario overwrites with post-upload finals.
      inputs: state.initialInputs,
    };
    return {
      ...prev,
      provider: "liveavatar",
      // Clears the other providers' blocks in case the scenario just
      // switched providers, derived from the provider spec registry.
      ...strippedForeignFields("liveavatar"),
      // LA-only fields.
      avatar_id: state.avatarId || undefined,
      voice_id: state.voiceId || undefined,
      language: state.language || undefined,
      // Preserve backend-owned session fields.
      context_id: prev.context_id,
      mode: prev.mode,
      llm_configuration_id: prev.llm_configuration_id,
      // Editor-owned top-level fields.
      publish_mode: state.publishMode,
      auto_start: state.autoStart,
      cta_type: state.ctaType,
      cta_url: state.ctaType === "sales" ? state.ctaUrl.trim() : "",
      redirect_url: state.redirectUrl.trim(),
      context_block: nextBlock,
      display: state.display,
    };
  },
};